import React, { useEffect, useState } from 'react'
import { Box, Text, Avatar, Accordion, AccordionItem, AccordionButton, AccordionIcon, AccordionPanel, Checkbox, Flex, Spacer, Tooltip } from '@chakra-ui/react'
import { InfoOutlineIcon } from '@chakra-ui/icons'

import currencySymbol from 'utils/functions/currencySymbolMap/currencySymbol'
import { useOfferingProvider } from 'providers/offeringProvider/OfferingProvider'
import { IBankData } from 'providers/offeringProvider/IOfferingProvider.d'
import getAvatar from '../../BankAccordion/bankAvatar'


interface BankAccordionProps {
  isPhysicalSubscription: boolean;
  name: string;
  accountsBank: IBankData[];
  bankId: number;
  isPlanChoice?: boolean;
  viewPage?: string;
  setLastSelectAccount?: Function;
}

const BankAccordion: React.FC<BankAccordionProps> = ({ isPhysicalSubscription, setLastSelectAccount, name, accountsBank, bankId, isPlanChoice = false }) => {
  const { accounts, selectedOfferings, setSelectedAccounts } = useOfferingProvider()
  const bankList = accounts
  const disableAccounts = isPlanChoice
  const positionElement = bankList.findIndex(item => item.bankId === bankId)
  const [_checkedItems, _setCheckedItems] = useState(selectedOfferings.accounts  ? accountsBank : accountsBank.map(item => ({ ...item, checked: false })))
  const accordionProps = disableAccounts ? {} : { allowMultiple: true, defaultIndex: [0] }
  const avatar = getAvatar(name)

  const allAccountsInDollars = accountsBank.every(account => account.currency === 'USD')

  const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    if(setLastSelectAccount){
      setLastSelectAccount({indexParent: positionElement, indexChild: index})
    }
    
    const copyChekedItems = [..._checkedItems]
    _checkedItems[index].checked = e.target.checked
    _setCheckedItems(copyChekedItems)
  }

  const handleCheckboxAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if(setLastSelectAccount){
      setLastSelectAccount({indexParent: positionElement})
    }
    _setCheckedItems(_checkedItems.map((item: IBankData) => ({ ...item, checked: e.target.checked })))
  }

  const allChecked = disableAccounts || _checkedItems?.every((item: IBankData) => item.checked === true)
  const isIndeterminate = _checkedItems?.some((item: IBankData) => item.checked) && !allChecked
  const planChoiceBgColor = disableAccounts ? 'blackAlpha.500' : ''
  const planChoiceBgColorHolver = disableAccounts ? { bg: 'blackAlpha.500' } : {}
  const planChoiceTooltipText = 'Podrás elegir cuentas de este banco y utilizarlas con nuestros servicios gratuitos una vez se habiliten las elegidas en tu plan'
  const allCheckedBgColor = allChecked || isIndeterminate ? 'secondary.40' : ''
  const allCheckedBgColorHover = allChecked || isIndeterminate ? { bg: 'secondary.40' } : {}
  const physicalSubscriptionTooltipText = 'El banco requiere que presentes documentación en sucursal para operar con sus cuentas. Accederás a la descarga de estos documentos más adelante'

  useEffect(() => {
    if (_checkedItems) {
      const copycheckedItems = [...bankList]
      copycheckedItems[positionElement].accounts = _checkedItems
      setSelectedAccounts(copycheckedItems)
    }
  }, [_checkedItems])

  return (
    <Accordion {...accordionProps} border='1px solid' borderColor='secondary.60' borderRadius='lg' mb={4} boxShadow='md' overflow='hidden'>
      <AccordionItem borderTop={0} borderBottom={0} isDisabled={disableAccounts}>
        <Text borderTopRightRadius='lg' borderTopLeftRadius='lg'>
          <AccordionButton
            borderBottom='1px solid'
            borderBottomColor='neutral.90'
            py={0}
            bg={planChoiceBgColor || allCheckedBgColor}
            _hover={planChoiceBgColorHolver || allCheckedBgColorHover}
          >
            <Box w='100%'>
              <Flex alignItems='center'>
                <Box>
                  <Checkbox
                    isChecked={allChecked}
                    isIndeterminate={isIndeterminate}
                    onChange={e => handleCheckboxAll(e)}
                    data-testid='all-checkbox'
                    isDisabled={allAccountsInDollars}
                  >
                    <Box alignItems='center' display='flex' flexDir='row'>
                      <Avatar name={name} src={avatar} m={3} size='sm' />
                      <Box textStyle='h6'>{name}</Box>
                    </Box>
                  </Checkbox>
                </Box>
                <Spacer />
                {isPlanChoice &&
                  <Box textStyle='bodyMd'>
                    Lo elegiste para tu plan
                    <Tooltip
                      label={planChoiceTooltipText}
                      aria-label='A tooltip'
                      placement='top'
                    >
                      <InfoOutlineIcon mx={2} />
                    </Tooltip>
                  </Box>
                }
                <Spacer />
                <Flex>
                  {disableAccounts ?
                    <Box textStyle='labelMd'>No ofrece servicios gratuitos</Box>
                    :
                    <Box>
                      <Text
                        fontWeight={400}
                        fontSize={12}
                        display='inline'
                        visibility={isPhysicalSubscription ? 'visible' : 'hidden'}
                      >
                        Requerirá suscripción física
                      </Text>
                      {isPhysicalSubscription &&
                        <Tooltip
                          label={physicalSubscriptionTooltipText}
                          aria-label='A tooltip'
                          placement='top'
                        >
                          <InfoOutlineIcon mx={2} />
                        </Tooltip>
                      }
                    </Box>
                  }
                  <AccordionIcon fontSize='25px' color='primary.500' borderRadius='8px' />
                </Flex>
              </Flex>
            </Box>
          </AccordionButton>
        </Text>
        <AccordionPanel p={0}>
           <Flex direction='column'>
            {accountsBank?.map((accountBank: IBankData, index) => {

              return (
              <Box key={accountBank.accountId} px={4} py={4} _notLast={{ borderBottom: '1px solid', borderBottomColor: 'grey.60' }} bg={_checkedItems[index]?.checked ? 'secondary.40' : ''}>
                <Checkbox
                  key={accountBank.accountId}
                  isChecked={_checkedItems[index]?.checked}
                  onChange={e => handleCheckbox(e, index)}
                  data-testid={`account_${accountBank.accountId}`}
                  isDisabled={allAccountsInDollars}
                >
                  <Flex direction='row'>
                    <Box ml='48px'>{accountBank.type}</Box>&nbsp;
                    <Box w={12} justifyContent='flex-end' display='flex' ml='48px'>{currencySymbol(accountBank.currency) || '$'}</Box>&nbsp;
                    <Box ml='48px'>{accountBank.cbu}</Box>
                  </Flex>
                </Checkbox>
              </Box>
              )
            })}
          </Flex> 
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  )
}

export default BankAccordion
